<script lang="ts">
/**  Any config options required for the OAuth2 flow */
export type OAuth2Options = Pick<ApiClientConfiguration, 'oauth2RedirectUri'>
</script>

<script setup lang="ts">
import { ScalarButton, useLoadingState } from '@scalar/components'
import type { ApiClientConfiguration } from '@scalar/types/api-reference'
import { pkceOptions } from '@scalar/types/entities'
import { useToasts } from '@scalar/use-toasts'
import type { SecretsOAuthFlows } from '@scalar/workspace-store/entities/auth'
import type {
  ApiReferenceEvents,
  WorkspaceEventBus,
} from '@scalar/workspace-store/events'
import {
  getEnvironmentVariables,
  type OAuthFlowAuthorizationCodeSecret,
  type OAuthFlowClientCredentialsSecret,
  type OAuthFlowPasswordSecret,
  type OAuthFlowsObjectSecret,
  type SecuritySchemeObjectSecret,
} from '@scalar/workspace-store/request-example'
import type { XScalarEnvironment } from '@scalar/workspace-store/schemas/extensions/document/x-scalar-environments'
import type { XScalarCredentialsLocation } from '@scalar/workspace-store/schemas/extensions/security/x-scalar-credentials-location'
import { type XusePkce } from '@scalar/workspace-store/schemas/extensions/security/x-use-pkce'
import type {
  OAuthFlow,
  ServerObject,
} from '@scalar/workspace-store/schemas/v3.1/strict/openapi-document'
import { computed, ref, watch } from 'vue'

import OAuthScopesInput from '@/v2/blocks/scalar-auth-selector-block/components/OAuthScopesInput.vue'
import {
  authorizeOauth2,
  refreshOauth2Token,
} from '@/v2/blocks/scalar-auth-selector-block/helpers/oauth'
import { resolveDefaultOAuth2RedirectUri } from '@/v2/blocks/scalar-auth-selector-block/helpers/resolve-default-oauth2-redirect-url'
import { DataTableRow } from '@/v2/components/data-table'

import RequestAuthDataTableInput from './RequestAuthDataTableInput.vue'

const {
  environment,
  flows,
  type,
  scheme,
  selectedScopes: selectedScopesProp,
  server,
  proxyUrl,
  eventBus,
  name,
  options = {},
} = defineProps<{
  /** Current environment configuration */
  environment: XScalarEnvironment
  /** OAuth flows */
  flows: OAuthFlowsObjectSecret
  /** Type of the OAuth flow */
  type: keyof OAuthFlowsObjectSecret
  /** Selected scopes */
  selectedScopes: string[]
  /** Security scheme */
  scheme: SecuritySchemeObjectSecret
  /** Current server configuration */
  server: ServerObject | null
  /** Proxy URL */
  proxyUrl: string
  /** Name of the security scheme */
  name: string
  /** Event bus for authentication updates */
  eventBus: WorkspaceEventBus
  /**  Any config options required for the OAuth2 flow */
  options?: OAuth2Options
}>()

const emits = defineEmits<{
  (
    e: 'update:selectedScopes',
    payload: Pick<
      ApiReferenceEvents['auth:update:selected-scopes'],
      'scopes' | 'newScopePayload'
    >,
  ): void
}>()

const loader = useLoadingState()
const { toast } = useToasts()

/** The current OAuth flow based on the selected type */
const flow = computed(() => flows[type]!)
type NonImplicitFlow =
  | OAuthFlowPasswordSecret
  | OAuthFlowClientCredentialsSecret
  | OAuthFlowAuthorizationCodeSecret

/** We filter selected scopes to only include scopes that are in this flow*/
const selectedScopes = computed(() =>
  selectedScopesProp.filter((scope) => scope in (flow.value.scopes ?? {})),
)

/** Updates the security scheme base */
const handleOauth2Update = (
  payload: Partial<OAuthFlow & XScalarCredentialsLocation>,
): void => {
  // OpenIdConnect uses the secrets update for all
  if (scheme.type === 'openIdConnect') {
    return handleOauth2SecretsUpdate(payload)
  }

  eventBus.emit('auth:update:security-scheme', {
    payload: {
      type: scheme.type,
      flows: {
        [type]: payload,
      },
    },
    name,
  })
}

/** Updates the flow secrets */
const handleOauth2SecretsUpdate = (
  payload: Omit<Partial<SecretsOAuthFlows[keyof SecretsOAuthFlows]>, 'type'>,
): void =>
  eventBus.emit('auth:update:security-scheme-secrets', {
    payload: {
      type: scheme.type,
      [type]: payload,
    },
    name,
  })

/** Clears the flow secrets */
const clearOauth2Secrets = (): void => {
  if (loader.isLoading) {
    return
  }
  eventBus.emit('auth:clear:security-scheme-secrets', {
    name,
  })
}

/** Clears stored access and refresh tokens (authorized state) */
const handleClearAccessTokens = (): void => {
  if (loader.isLoading) {
    return
  }
  handleOauth2SecretsUpdate({
    'x-scalar-secret-token': '',
    'x-scalar-secret-refresh-token': '',
  })
}

/** Track redirect URI prefill per flow instance to support document switching */
const prefilledFlowIdentity = ref<string | null>(null)
const hasHandledRedirectPrefill = ref(false)

const resolveFlowIdentity = (
  currentFlow: OAuthFlowsObjectSecret[keyof OAuthFlowsObjectSecret] | undefined,
): string =>
  JSON.stringify({
    type,
    authorizationUrl:
      currentFlow && 'authorizationUrl' in currentFlow
        ? currentFlow.authorizationUrl
        : '',
    tokenUrl:
      currentFlow && 'tokenUrl' in currentFlow ? currentFlow.tokenUrl : '',
    refreshUrl: currentFlow?.refreshUrl ?? '',
    scopes: Object.keys(currentFlow?.scopes ?? {}),
  })

/** Default the redirect-uri to the current origin if we have access to window */
watch(
  () => flow.value,
  (currentFlow) => {
    if (!currentFlow || !('x-scalar-secret-redirect-uri' in currentFlow)) {
      return
    }

    const flowIdentity = resolveFlowIdentity(currentFlow)
    if (prefilledFlowIdentity.value !== flowIdentity) {
      prefilledFlowIdentity.value = flowIdentity
      hasHandledRedirectPrefill.value = false
    }

    if (hasHandledRedirectPrefill.value) {
      return
    }

    const newRedirectUri = (currentFlow as OAuthFlowAuthorizationCodeSecret)[
      'x-scalar-secret-redirect-uri'
    ]
    const defaultRedirectUri = resolveDefaultOAuth2RedirectUri(options)

    hasHandledRedirectPrefill.value = true

    if (newRedirectUri || !defaultRedirectUri) {
      return
    }

    handleOauth2SecretsUpdate({
      'x-scalar-secret-redirect-uri': defaultRedirectUri,
    })
  },
  { immediate: true },
)

/**
 * Authorizes the user using the specified OAuth flow.
 * Opens the appropriate OAuth dialog and/or performs the token exchange.
 */
const handleAuthorize = async (): Promise<void> => {
  if (loader.isLoading) {
    return
  }

  loader.start()

  const [error, tokens] = await authorizeOauth2(
    flows,
    type,
    selectedScopes.value,
    server,
    proxyUrl,
    getEnvironmentVariables(environment),
  )

  await loader.clear()

  if (tokens?.accessToken) {
    handleOauth2SecretsUpdate({
      'x-scalar-secret-token': tokens.accessToken,
      ...(tokens.refreshToken
        ? { 'x-scalar-secret-refresh-token': tokens.refreshToken }
        : {}),
    })
  } else {
    console.error(error)
    toast(error?.message ?? 'Failed to authorize', 'error')
  }
}

/** Whether the current flow supports refreshing the access token */
const supportsRefreshToken = computed(() => type !== 'implicit')

/**
 * Refresh URL placeholder, shows tokenUrl as hint if refreshUrl is not specified.
 * This helps users understand that tokenUrl will be used as fallback.
 */
const refreshUrlPlaceholder = computed(() => {
  if ('tokenUrl' in flow.value && flow.value.tokenUrl) {
    return flow.value.tokenUrl
  }
  return 'https://galaxy.scalar.com/oauth/refresh'
})

/**
 * Uses the stored refresh token to obtain a new access token
 * via grant_type=refresh_token.
 */
const handleRefresh = async (): Promise<void> => {
  if (loader.isLoading || type === 'implicit') {
    return
  }

  loader.start()

  const [error, tokens] = await refreshOauth2Token(
    flows,
    type,
    proxyUrl,
    server,
    getEnvironmentVariables(environment),
  )

  await loader.clear()

  if (tokens?.accessToken) {
    handleOauth2SecretsUpdate({
      'x-scalar-secret-token': tokens.accessToken,
      ...(tokens.refreshToken
        ? { 'x-scalar-secret-refresh-token': tokens.refreshToken }
        : {}),
    })
  } else {
    console.error(error)
    toast(error?.message ?? 'Failed to refresh token', 'error')
  }
}

/** Updates the secret location */
const handleSecretLocationUpdate = (value: string): void => {
  const credentialsLocation = value === 'body' ? 'body' : 'header'

  if (scheme.type !== 'openIdConnect') {
    handleOauth2Update({
      'x-scalar-credentials-location': credentialsLocation,
    })
  }

  handleOauth2SecretsUpdate({
    'x-scalar-credentials-location': credentialsLocation,
  })
}
</script>

<template>
  <!-- Access Token Display: Shows when user is already authorized -->
  <template v-if="Boolean(flow['x-scalar-secret-token'])">
    <DataTableRow>
      <RequestAuthDataTableInput
        class="border-r-transparent"
        :environment
        :modelValue="flow['x-scalar-secret-token']"
        placeholder="QUxMIFlPVVIgQkFTRSBBUkUgQkVMT05HIFRPIFVT"
        type="password"
        @update:modelValue="
          (v) => handleOauth2SecretsUpdate({ 'x-scalar-secret-token': v })
        ">
        Access Token
      </RequestAuthDataTableInput>
    </DataTableRow>

    <DataTableRow v-if="supportsRefreshToken">
      <RequestAuthDataTableInput
        class="border-r-transparent"
        :environment
        :modelValue="flow.refreshUrl ?? ''"
        :placeholder="refreshUrlPlaceholder"
        @update:modelValue="(v) => handleOauth2Update({ refreshUrl: v })">
        Refresh URL
      </RequestAuthDataTableInput>
    </DataTableRow>

    <DataTableRow class="min-w-full">
      <div class="flex h-8 items-center justify-end gap-2 border-t">
        <ScalarButton
          v-if="supportsRefreshToken"
          class="p-0 px-2 py-0.5"
          :loader
          size="sm"
          variant="outlined"
          @click="handleRefresh">
          Refresh
        </ScalarButton>
        <ScalarButton
          class="mr-1 p-0 px-2 py-0.5"
          :disabled="loader.isLoading"
          size="sm"
          variant="outlined"
          @click="handleClearAccessTokens">
          Clear
        </ScalarButton>
      </div>
    </DataTableRow>
  </template>

  <!-- Authorization Form: Shows when user needs to authorize -->
  <template v-else>
    <DataTableRow>
      <RequestAuthDataTableInput
        v-if="'authorizationUrl' in flow"
        containerClass="border-r-0"
        :environment
        :modelValue="flow['x-scalar-secret-auth-url'] ?? ''"
        placeholder="https://galaxy.scalar.com/authorize"
        @update:modelValue="
          (v) => {
            handleOauth2SecretsUpdate({ 'x-scalar-secret-auth-url': v })
            handleOauth2Update({ authorizationUrl: v })
          }
        ">
        Auth URL
      </RequestAuthDataTableInput>

      <RequestAuthDataTableInput
        v-if="'tokenUrl' in flow"
        :environment
        :modelValue="flow['x-scalar-secret-token-url'] ?? ''"
        placeholder="https://galaxy.scalar.com/token"
        @update:modelValue="
          (v) => {
            handleOauth2SecretsUpdate({ 'x-scalar-secret-token-url': v })
            handleOauth2Update({ tokenUrl: v })
          }
        ">
        Token URL
      </RequestAuthDataTableInput>
    </DataTableRow>

    <DataTableRow v-if="'x-scalar-secret-redirect-uri' in flow">
      <RequestAuthDataTableInput
        :environment
        :modelValue="flow['x-scalar-secret-redirect-uri']"
        placeholder="Optional redirect URL"
        @update:modelValue="
          (v) => {
            hasHandledRedirectPrefill = true
            handleOauth2SecretsUpdate({ 'x-scalar-secret-redirect-uri': v })
          }
        ">
        Redirect URL
      </RequestAuthDataTableInput>
    </DataTableRow>

    <template
      v-if="
        'x-scalar-secret-username' in flow && 'x-scalar-secret-password' in flow
      ">
      <DataTableRow>
        <RequestAuthDataTableInput
          class="text-c-2"
          :environment
          :modelValue="flow['x-scalar-secret-username']"
          placeholder="janedoe"
          @update:modelValue="
            (v) => handleOauth2SecretsUpdate({ 'x-scalar-secret-username': v })
          ">
          Username
        </RequestAuthDataTableInput>
      </DataTableRow>

      <DataTableRow>
        <RequestAuthDataTableInput
          :environment
          :modelValue="flow['x-scalar-secret-password']"
          placeholder="********"
          type="password"
          @update:modelValue="
            (v) => handleOauth2SecretsUpdate({ 'x-scalar-secret-password': v })
          ">
          Password
        </RequestAuthDataTableInput>
      </DataTableRow>
    </template>

    <DataTableRow>
      <RequestAuthDataTableInput
        :environment
        :modelValue="flow['x-scalar-secret-client-id']"
        placeholder="12345"
        @update:modelValue="
          (v) => handleOauth2SecretsUpdate({ 'x-scalar-secret-client-id': v })
        ">
        Client ID
      </RequestAuthDataTableInput>
    </DataTableRow>

    <DataTableRow v-if="'x-scalar-secret-client-secret' in flow">
      <RequestAuthDataTableInput
        :environment
        :modelValue="flow['x-scalar-secret-client-secret']"
        placeholder="XYZ123"
        type="password"
        @update:modelValue="
          (v) =>
            handleOauth2SecretsUpdate({ 'x-scalar-secret-client-secret': v })
        ">
        Client Secret
      </RequestAuthDataTableInput>
    </DataTableRow>

    <DataTableRow v-if="'x-usePkce' in flow">
      <RequestAuthDataTableInput
        :enum="pkceOptions"
        :environment
        :modelValue="flow['x-usePkce']"
        readOnly
        @update:modelValue="
          (v) =>
            handleOauth2Update({
              'x-usePkce': v as XusePkce['x-usePkce'],
            })
        ">
        Use PKCE
      </RequestAuthDataTableInput>
    </DataTableRow>

    <!-- Credentials Location -->
    <DataTableRow v-if="type !== 'implicit'">
      <RequestAuthDataTableInput
        :enum="['header', 'body']"
        :environment
        :modelValue="
          (flow as NonImplicitFlow)['x-scalar-credentials-location'] || 'header'
        "
        placeholder="header"
        readOnly
        @update:modelValue="(v) => handleSecretLocationUpdate(v)">
        Credentials Location
      </RequestAuthDataTableInput>
    </DataTableRow>

    <!-- Scopes -->
    <DataTableRow>
      <OAuthScopesInput
        :flow
        :flowType="type"
        :selectedScopes
        @update:selectedScopes="(v) => emits('update:selectedScopes', v)" />
    </DataTableRow>

    <DataTableRow class="min-w-full">
      <div class="flex h-8 w-full items-center justify-end border-t">
        <!-- Allow clearing the oauth section and going back to discovery -->
        <ScalarButton
          v-if="scheme.type === 'openIdConnect'"
          class="mr-1 p-0 px-2 py-0.5"
          :disabled="loader.isLoading"
          size="sm"
          variant="outlined"
          @click="clearOauth2Secrets">
          Clear
        </ScalarButton>

        <ScalarButton
          class="mr-0.75 p-0 px-2 py-0.5"
          :loader
          size="sm"
          variant="outlined"
          @click="handleAuthorize">
          Authorize
        </ScalarButton>
      </div>
    </DataTableRow>
  </template>
</template>
